'use client';

import type { ReactNode, RefObject } from 'react';
import { useTranslation } from 'react-i18next';

import { DataTestIds } from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSPrimitiveButton } from '@repo/sds/SDSPrimitiveButton';
import { SDSText } from '@repo/sds/SDSText';
import { MfaOtpInput } from '@repo/ui/pages/settings-pages/security/mfa-otp-input';
import { formatResendClock } from '@repo/ui/pages/settings-pages/security/mfa-sms-setup-utils';

import { useResendCountdown } from './use-resend-countdown';
import { SMS_CODE_DIGITS, type SmsMarketingBusyAction } from './use-sms-marketing-signup-flow';

const ids = DataTestIds.SMS_MARKETING;

interface SmsCodeEntryProps {
  /** What the person is asked to do; left out, it says where the code went. */
  instruction?: string;
  code: string;
  codeRef: RefObject<HTMLDivElement | null>;
  maskedPhone: string;
  isDisabled: boolean;
  onChangeCode: (code: string) => void;
}

/** Says where the code went and takes it. */
export function SmsCodeEntry({ instruction, code, codeRef, maskedPhone, isDisabled, onChangeCode }: SmsCodeEntryProps) {
  const { t } = useTranslation('common');
  return (
    <>
      <SDSText
        type="body-medium"
        text={instruction ?? t('smsMarketing.codeSent', { digits: SMS_CODE_DIGITS, phone: maskedPhone })}
      />
      <div ref={codeRef}>
        <MfaOtpInput value={code} disabled={isDisabled} onChange={onChangeCode} dataTestId={ids.CODE_INPUT} />
      </div>
    </>
  );
}

interface SmsCodeActionsProps {
  busyAction: SmsMarketingBusyAction | undefined;
  resendAvailableAt: Date;
  onResend: () => void;
  onChangeNumber: () => void;
  /** Further actions the surface offers next to resend and change number. */
  children?: ReactNode;
}

export function SmsCodeActions({
  busyAction,
  resendAvailableAt,
  onResend,
  onChangeNumber,
  children,
}: SmsCodeActionsProps) {
  const { t } = useTranslation('common');
  const secondsLeft = useResendCountdown(resendAvailableAt);
  const isBusy = busyAction !== undefined;
  const isResending = busyAction === 'resend';

  return (
    <SDSFlexbox direction="row" justifyContent="space-between" wrap="wrap" gap="no-design-8">
      <SDSButton
        type="button"
        mode="ghost"
        size="small"
        text={
          secondsLeft > 0
            ? t('smsMarketing.resendIn', { time: formatResendClock(secondsLeft) })
            : t('smsMarketing.resend')
        }
        isDisabled={!isResending && (secondsLeft > 0 || isBusy)}
        isSubmitting={isResending}
        onClick={onResend}
        dataTestId={ids.RESEND_BUTTON}
      />
      <SDSButton
        type="button"
        mode="ghost"
        size="small"
        text={t('smsMarketing.changeNumber')}
        isDisabled={isBusy}
        onClick={onChangeNumber}
        dataTestId={ids.CHANGE_NUMBER_BUTTON}
      />
      {children}
    </SDSFlexbox>
  );
}

interface SmsCodeWaitingNoticeProps {
  /** As shown to the person, already masked and formatted. */
  phone: string;
  isDisabled: boolean;
  onEnterCode: () => void;
}

/** Says a code is still waiting after its dialog was closed, and reopens it. */
export function SmsCodeWaitingNotice({ phone, isDisabled, onEnterCode }: SmsCodeWaitingNoticeProps) {
  const { t } = useTranslation('common');
  return (
    <SDSFlexbox direction="row" gap="no-design-8" alignItems="center" wrap="wrap" dataTestId={ids.CODE_WAITING}>
      <SDSText type="body-small" color="muted" text={t('smsMarketing.codeWaiting', { phone })} />
      <SDSPrimitiveButton isDisabled={isDisabled} onClick={onEnterCode} dataTestId={ids.ENTER_CODE_BUTTON}>
        <SDSText color="primary" type="body-small" text={t('smsMarketing.enterCode')} />
      </SDSPrimitiveButton>
    </SDSFlexbox>
  );
}
